import React from "react";
import cx from "classnames";
import gs, { colors } from "gstyles";
import Image from "components/common/Image";
import { IconName } from "gstyles/styleguide/icons";
interface LayoutProps {
  iconName?: IconName;
  title?: string;
  description?: string;
  banner?: string;
  titleStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  /** Nền của cả section (mặc định trắng) — dùng để tạo nhịp xen kẽ trên trang chủ */
  className?: string;
  /** Nội dung bên phải header (ví dụ link "Xem tất cả", chip filter) */
  extra?: React.ReactNode;
  children?: React.ReactNode;
}

/**
 * CMS lưu tên dải bằng CHỮ HOA ("ƯU ĐÃI TRẢI NGHIỆM") còn bản dịch là chữ thường ("Địa điểm khác")
 * → trang chủ lẫn 2 kiểu. Chuẩn hoá về dạng câu: chỉ viết hoa chữ cái đầu.
 */
const toSentenceCase = (s: string) => {
  if (!s || s !== s.toLocaleUpperCase("vi") || s === s.toLocaleLowerCase("vi")) return s;
  const lower = s.toLocaleLowerCase("vi");
  return lower.charAt(0).toLocaleUpperCase("vi") + lower.slice(1);
};

const Layout: React.FC<LayoutProps> = ({
  iconName = "sun-fog",
  title = "",
  description = "",
  banner = "",
  titleStyle = {},
  style = {},
  className = "",
  extra,
  children,
}) => {
  return (
    // Chỉ dùng nền trắng khi section không tự đặt nền: cx("bg-white", "bg-[#fff7ee]") để cả 2 class
    // → bg-white thắng theo thứ tự CSS, nhịp nền xen kẽ trên trang chủ trước đây không hiện
    <div className={cx(className || "bg-white")}>
      {/* Nhịp dọc đều: mỗi section py cố định, không cộng dồn pb + mb như trước (khoảng trống ~100px) */}
      <div className="container mx-auto pb-5 md:pb-8" style={style}>
        <div className="flex items-center justify-between gap-3 md:gap-4 px-4 md:px-6 pt-5 pb-3 md:pt-8 md:pb-5">
          <div className="flex items-center md:items-start gap-2.5 md:gap-3 min-w-0">
            <div className="flex items-center shrink-0 md:mt-0.5">
              {iconName &&
                gs.icons({ name: iconName, width: 24, color: colors.primary })}
            </div>
            <div className="min-w-0">
              {/* Tiêu đề section phải mạnh hơn tiêu đề card: bỏ uppercase + opacity, chữ đậm & lớn hơn */}
              <h2
                className="!mb-0 text-[18px] md:text-[22px] leading-tight font-bold text-black01 tracking-[-0.01em]"
                style={titleStyle}
              >
                {toSentenceCase(title)}
              </h2>
              {/* key={description} để mỗi lần câu giới thiệu đổi thì fade vào mềm mại.
                  Hoặc đọc được, hoặc không hiện: trước đây gray-500 × opacity 0.7 × keyframe 0.7 ≈ mờ hẳn */}
              <div
                key={description}
                className="hidden md:block mt-1 text-gray-500 text-[15px] tagline-fade"
              >
                {description}
              </div>
            </div>
          </div>
          {extra ? <div className="shrink-0 flex items-center gap-2">{extra}</div> : null}
        </div>
        {banner && (
          <div className="px-4 md:px-6 mb-4">
            <Image
              src={banner}
              alt="Banner"
              wrapClassName="w-full max-w-[343px] aspect-[343/100] rounded-md object-cover"
            />
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default Layout;
